import { useEffect, useState, useMemo } from "react";
import { api, pct, rollingMonthLabels, rollingPeriodLabel, MONTH_KEYS } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import EmployeeProjectsModal from "../components/EmployeeProjectsModal";
import ExportBar from "../components/ExportBar";

const COLORS = { overload: "#dc2626", underload: "#d97706", optimal: "#16a34a", unassigned: "#64748b" };
const NAVY = "#1a3666";
const STYLES = {
  gridKpi: { gridTemplateColumns: "repeat(auto-fit,minmax(148px,1fr))" },
  gridCharts: { gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" },
  chartMargin: { left: 20 },
  navyFg: { color: NAVY },
  purpleDot: { r: 5 },
};
const BAR_Y_TICK = { fontSize: 11 };
const BAR_X_TICK_SMALL = { fontSize: 10 };

function Kpi({ label, value, note, tone }) {
  const border = { total: "#00a3a3", over: "#dc2626", under: "#d97706", not: "#7c3aed", opt: "#16a34a", orange: "#f07000" }[tone] || "#00a3a3";
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200 relative overflow-hidden" data-testid={`kpi-${tone || "card"}`}>
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: border }} />
      <div className="text-[11px] text-slate-500 font-medium mb-1">{label}</div>
      <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{note}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [alertFilter, setAlertFilter] = useState({ portfolio: "", cc: "", status: "" });

  useEffect(() => {
    api.get("/data/latest").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  const s = data?.stats;
  const employees = data?.employees || [];

  const [m1, m2, m3] = rollingMonthLabels(3); // e.g. ["Mar","Apr","May"] if today is May
  const periodLabel = rollingPeriodLabel(3);

  const utilPerEmp = useMemo(() =>
    employees.slice().sort((a, b) => b.avg - a.avg).slice(0, 15).map((e) => ({
      name: e.name.split(" ")[0], [m1]: Math.round(e.apr * 100), [m2]: Math.round(e.may * 100), [m3]: Math.round(e.jun * 100),
    })), [employees, m1, m2, m3]);

  const statusPie = useMemo(() => s ? [
    { name: "Overloaded", value: s.overload, fill: COLORS.overload },
    { name: "Optimal", value: s.optimal, fill: COLORS.optimal },
    { name: "Underloaded", value: s.underload, fill: COLORS.underload },
    { name: "Not Assigned", value: s.unassigned, fill: COLORS.unassigned },
  ] : [], [s]);

  const portfolioAgg = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      const k = e.portfolio || "Unassigned";
      m[k] = m[k] || { name: k, avg: 0, n: 0, over: 0, under: 0 };
      m[k].avg += e.avg; m[k].n += 1;
      if (e.status === "overload") m[k].over += 1;
      if (e.status === "underload") m[k].under += 1;
    });
    return Object.values(m).map((p) => ({ ...p, AvgUtil: Math.round((p.avg / p.n) * 100), Overloaded: p.over, Underloaded: p.under }));
  }, [employees]);

  const monthlyTrend = useMemo(() => {
    if (!employees.length) return [];
    const avg = (k) => Math.round((employees.reduce((a, e) => a + e[k], 0) / employees.length) * 100);
    return [{ month: m1, value: avg("apr") }, { month: m2, value: avg("may") }, { month: m3, value: avg("jun") }];
  }, [employees, m1, m2, m3]);

  const alerts = useMemo(() => {
    const base = employees.filter((e) => e.status === "overload" || e.status === "underload");
    const f = alertFilter;
    return base.filter((e) => {
      if (f.portfolio && !(e.portfolio || "").toLowerCase().includes(f.portfolio.toLowerCase())) return false;
      if (f.cc && !(e.cc || "").toLowerCase().includes(f.cc.toLowerCase())) return false;
      if (f.status && e.status !== f.status) return false;
      return true;
    }).sort((a, b) => b.avg - a.avg);
  }, [employees, alertFilter]);

  const portfolioOptions = useMemo(
    () => Array.from(new Set(employees.map((e) => e.portfolio || "Unassigned"))).sort(),
    [employees]
  );
  const ccOptions = useMemo(
    () => Array.from(new Set(employees.map((e) => e.cc).filter(Boolean))).sort(),
    [employees]
  );

  if (err) return <div data-testid="dashboard-error" className="text-red-600">{err}</div>;
  if (!data) return <div data-testid="dashboard-loading" className="text-slate-500">Loading…</div>;
  if (data.empty) return (
    <div data-testid="dashboard-empty" className="bg-white border border-slate-200 rounded-xl p-10 text-center">
      <h2 className="text-lg font-semibold text-slate-800 mb-2">No data yet</h2>
      <p className="text-slate-500 text-sm">Upload a Billability Excel file from the Upload tab to get started.</p>
    </div>
  );

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>Utilization Overview</h2>
          <p className="text-sm text-slate-500 mt-0.5">{periodLabel} · {data.filename}</p>
        </div>
      </div>

      <div className="grid gap-3 mb-5" style={STYLES.gridKpi}>
        <Kpi tone="total" label="Total Resources" value={s.total} note="" />
        <Kpi tone="orange" label="Team Avg" value={pct(s.avgUtil)} note={`${m1}–${m3} avg`} />
        <Kpi tone="over" label="Overloaded" value={s.overload} note=">100%" />
        <Kpi tone="under" label="Underloaded" value={s.underload} note="<75%" />
        <Kpi tone="not" label="Not Assigned" value={s.unassigned} note="0%" />
        <Kpi tone="opt" label="Optimal" value={s.optimal} note="75–100%" />
      </div>

      <div className="grid gap-4 mb-5" style={STYLES.gridCharts}>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <h3 className="text-sm font-semibold mb-3" style={STYLES.navyFg}>Top Utilization per Employee</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={utilPerEmp} layout="vertical" margin={STYLES.chartMargin}>
              <XAxis type="number" tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="name" width={80} tick={BAR_Y_TICK} />
              <Tooltip />
              <Legend content={() => (
                <div style={{ textAlign: "center", fontSize: 12, paddingTop: 4 }}>
                  <span style={{ marginRight: 12 }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, background: "#00a3a3", marginRight: 4 }} />{m1}
                  </span>
                  <span style={{ marginRight: 12 }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, background: "#1a3666", marginRight: 4 }} />{m2}
                  </span>
                  <span>
                    <span style={{ display: "inline-block", width: 10, height: 10, background: "#f07000", marginRight: 4 }} />{m3}
                  </span>
                </div>
              )} />
              <Bar dataKey={m1} fill="#00a3a3" />
              <Bar dataKey={m2} fill="#1a3666" />
              <Bar dataKey={m3} fill="#f07000" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <h3 className="text-sm font-semibold mb-3" style={{ color: "#1a3666" }}>Monthly Trend (Team Avg)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => `${v}%`} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#7c3aed" strokeWidth={3} dot={STYLES.purpleDot} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-4 mb-5" style={STYLES.gridCharts}>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <h3 className="text-sm font-semibold mb-3" style={STYLES.navyFg}>Capacity Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={88}>
                {statusPie.map((e) => <Cell key={e.name} fill={e.fill} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <h3 className="text-sm font-semibold mb-3" style={{ color: "#1a3666" }}>Product Portfolio — Avg Utilization</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={portfolioAgg}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={BAR_X_TICK_SMALL} angle={-25} textAnchor="end" height={70} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="AvgUtil" fill="#00a3a3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-5" data-testid="capacity-alerts">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold shrink-0" style={{ color: "#1a3666" }}>
            ⚠ Capacity Alerts ({alerts.length})
          </h3>
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            <select value={alertFilter.portfolio} onChange={(e) => setAlertFilter((f) => ({ ...f, portfolio: e.target.value }))}
                    data-testid="ca-filter-portfolio"
                    className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white">
              <option value="">All Portfolios</option>
              {portfolioOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={alertFilter.cc} onChange={(e) => setAlertFilter((f) => ({ ...f, cc: e.target.value }))}
                    data-testid="ca-filter-cc"
                    className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white">
              <option value="">All CCs</option>
              {ccOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={alertFilter.status} onChange={(e) => setAlertFilter((f) => ({ ...f, status: e.target.value }))}
                    data-testid="ca-filter-status"
                    className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white">
              <option value="">All Statuses</option>
              <option value="overload">Overloaded</option>
              <option value="underload">Underloaded</option>
            </select>
            {(alertFilter.portfolio || alertFilter.cc || alertFilter.status) && (
              <button onClick={() => setAlertFilter({ portfolio: "", cc: "", status: "" })}
                      data-testid="ca-filter-clear"
                      className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
            )}
          </div>
          <ExportBar spec={() => ({
            title: "Capacity Alerts",
            subtitle: `${periodLabel} · ${data?.filename || ""}`,
            filters: alertFilter,
            columns: [
              { key: "name", label: "Employee" },
              { key: "portfolio", label: "Portfolio", get: (r) => r.portfolio || "Unassigned" },
              { key: "cc", label: "CC" },
              { key: "role", label: "Role", get: (r) => r.role || "—" },
              { key: "apr", label: `${m1} %`, get: (r) => pct(r.apr) },
              { key: "may", label: `${m2} %`, get: (r) => pct(r.may) },
              { key: "jun", label: `${m3} %`, get: (r) => pct(r.jun) },
              { key: "avg", label: "Avg %", get: (r) => pct(r.avg) },
              { key: "status", label: "Status", isStatus: true, get: (r) => r.status === "overload" ? "Overloaded" : r.status === "underload" ? "Underloaded" : r.status },
            ],
            rows: alerts,
            filename: "vSDI_Capacity_Alerts",
          })} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Employee</th>
                <th className="px-3 py-2 text-left">Portfolio</th>
                <th className="px-3 py-2 text-left">CC</th>
                <th className="px-3 py-2 text-center">{m1}</th>
                <th className="px-3 py-2 text-center">{m2}</th>
                <th className="px-3 py-2 text-center">{m3}</th>
                <th className="px-3 py-2 text-center">Avg</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((e) => (
                <tr key={`${e.name}-${e.cc}-${e.portfolio}`}
                    onClick={() => setSelectedEmp(e)}
                    className="border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition"
                    data-testid="alert-row"
                    title="Click to see project allocations">
                  <td className="px-3 py-2 font-medium">{e.name}</td>
                  <td className="px-3 py-2 text-slate-600">{e.portfolio || "Unassigned"}</td>
                  <td className="px-3 py-2 text-slate-600">{e.cc}</td>
                  <td className="px-3 py-2 text-center">{pct(e.apr)}</td>
                  <td className="px-3 py-2 text-center">{pct(e.may)}</td>
                  <td className="px-3 py-2 text-center">{pct(e.jun)}</td>
                  <td className="px-3 py-2 text-center font-semibold">{pct(e.avg)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      e.status === "overload" ? "bg-red-100 text-red-700" :
                      e.status === "underload" ? "bg-amber-100 text-amber-700" : "bg-slate-100"}`}>
                      {e.status === "overload" ? "HIGH" : "LOW"}
                    </span>
                  </td>
                </tr>
              ))}
              {!alerts.length && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No capacity alerts — team within thresholds.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EmployeeProjectsModal employee={selectedEmp} onClose={() => setSelectedEmp(null)} />
    </div>
  );
}
